import {
  CalendarAddIcon,
  Checkbox,
  DateInput,
  ErrorMessage,
  Input,
  ItemsArea,
  MessageBox,
  Select,
  TimeInput,
  ToggleButton,
} from "@jutro/ui";
import { OptionWithSideElements } from "@jutro/ui/dist/Select/tools";
import { Dayjs } from "dayjs";
import { isEqual } from "radash";
import { useRef } from "react";
import { Controller } from "react-hook-form";
import { EdmMedicalAppShiftWithoutVisitFragment } from "lib/graphql/megaSchema";
import { organizationClinicOption } from "lib/hooks/useVojevodshipOptions";
import { ButtonsController } from "views/StaffSchedule/StaffScheduleCreator/components/Modals/ButtonsController";
import { OverrideVisitDurationInputGroup } from "views/StaffSchedule/StaffScheduleCreator/components/Modals/ShiftActionsModal/OverrideVisitDurationInputGroup";
import { useGetHolidaysInChangeRange } from "views/StaffSchedule/StaffScheduleCreator/hooks/useGetHolidaysInChangeRange";
import { useShiftModalForm } from "views/StaffSchedule/StaffScheduleCreator/hooks/useShiftModalForm";
import {
  StaffOption,
  changeRepeatingOptions,
  daysOptions,
  isDayInRange,
  isMedicalAppShift,
  workPlaceOptions,
} from "views/StaffSchedule/StaffScheduleCreator/tools";
import { StaffScheduleDataTestId } from "views/StaffSchedule/StaffScheduleDataTestId";

type Props = {
  staffOptions: OptionWithSideElements<StaffOption>[];
  selectedBlockStaffId: string;
  selectedDay?: Dayjs;
  startDate: Dayjs;
  selectedShift: EdmMedicalAppShiftWithoutVisitFragment | undefined;
  selectedOrganizationId: string;
  organizationsClinicsOptions: organizationClinicOption[];
  isEditMode: boolean;
  isCreateMode: boolean;
  onCloseModal: () => void;
};

export const ShiftActionsModal = (props: Props) => {
  const {
    visitTypeOptions,
    control,
    errors,
    loading,
    handleSubmit,
    onSubmit,
    trigger,
    setValue,
    watch,
  } = useShiftModalForm(props);

  const {
    staffOptions,
    selectedBlockStaffId,
    startDate,
    selectedShift,
    selectedOrganizationId,
    organizationsClinicsOptions,
    isEditMode,
    isCreateMode,
    onCloseModal,
  } = props;

  const timeFromInputRef = useRef<HTMLInputElement>(null);
  const timeToInputRef = useRef<HTMLInputElement>(null);

  const {
    visitTypes: selectedVisitTypes,
    isCustomVisitDuration,
    changeRepeating: selectedChangeRepeating,
    dateStartFrom,
    dateEnded,
  } = watch();

  const holidaysInRange = useGetHolidaysInChangeRange({
    dateStartFrom,
    dateEnded,
    selectedChangeRepeating,
  });

  const isDeletable = Boolean(selectedShift?.state === "PRE_DELETED");

  const isActiveOrganization =
    isMedicalAppShift(selectedShift) &&
    selectedOrganizationId === selectedShift.organizationId;

  const isDeleteBlocked =
    selectedShift?.organizationId !== selectedOrganizationId;

  const {
    timeTo: timeToFieldError,
    timeFrom: timeFromFieldError,
    acceptingNfz: acceptingNfzFieldError,
    dateEnded: dateEndedError,
  } = errors;

  return (
    <>
      <form className="flex flex-col gap-4 px-4">
        <div className="flex gap-4">
          <Controller
            control={control}
            name="organizationId"
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <Select
                label="Lokalizacja*"
                disabled
                options={organizationsClinicsOptions}
                value={value}
                onChange={onChange}
                error={error?.message}
              />
            )}
          />

          <Controller
            control={control}
            name="workPlace"
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <Select
                data-testId={StaffScheduleDataTestId.WorkPlaceSelect}
                label="Miejsce wykonywania pracy*"
                placeholder="Wybierz z listy"
                disabled={isEditMode}
                options={workPlaceOptions}
                value={value}
                onChange={onChange}
                error={error?.message}
              />
            )}
          />
        </div>

        <div className="flex gap-4">
          <div className="flex-1">
            <Controller
              control={control}
              name="staff"
              render={({
                field: { onChange, value },
                fieldState: { error },
              }) => (
                <Select
                  data-testId={StaffScheduleDataTestId.DoctorSelect}
                  label="Przypisz do*"
                  placeholder="Wybierz z listy"
                  isSearchable={isCreateMode || !selectedBlockStaffId}
                  disabled={Boolean(selectedBlockStaffId) || isEditMode}
                  options={staffOptions}
                  value={value}
                  onChange={onChange}
                  error={error?.message}
                />
              )}
            />
          </div>

          <div className="flex flex-1 flex-col gap-1">
            <span className="font-paragraph-1 font-normal">
              Status personelu*
            </span>

            <div className="bg-jutro-new-warm-gray-50 flex gap-8 rounded-lg px-2">
              <Controller
                control={control}
                name="acceptingNfz"
                render={({
                  field: { onChange, value, ...restField },
                  fieldState: { error },
                }) => (
                  <div
                    data-testId={StaffScheduleDataTestId.AcceptingNfzCheckbox}
                  >
                    <Checkbox
                      label="Zgłoszony/a do NFZ"
                      checked={value}
                      disabled={isEditMode}
                      error={Boolean(error)}
                      onChange={() => {
                        onChange(!value);
                      }}
                      {...restField}
                    />
                  </div>
                )}
              />

              <Controller
                control={control}
                name="acceptingPriv"
                render={({ field: { onChange, value, ...restField } }) => (
                  <div
                    data-testId={StaffScheduleDataTestId.AcceptingPrivCheckbox}
                  >
                    <Checkbox
                      label="Obsługuje PRIV"
                      checked={value}
                      disabled={isEditMode}
                      error={Boolean(acceptingNfzFieldError)}
                      onChange={() => {
                        onChange(!value);
                        trigger();
                      }}
                      {...restField}
                    />
                  </div>
                )}
              />
            </div>
            {acceptingNfzFieldError?.message && (
              <ErrorMessage error={acceptingNfzFieldError.message} />
            )}
          </div>
        </div>

        <div className="flex gap-4">
          <Controller
            control={control}
            name="visitTypes"
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <div className="flex w-1/2 flex-col gap-2.5">
                <Select
                  data-testId={StaffScheduleDataTestId.VisitTypesSelect}
                  label="Dodaj typ wizyty*"
                  placeholder="Wybierz z listy"
                  disabled={isEditMode}
                  options={visitTypeOptions}
                  value={value}
                  onChange={(option) => {
                    if (!option) {
                      return;
                    }

                    onChange([...value, option]);
                  }}
                  onRemove={(option) => {
                    const updatedVisitTypes = value.filter(
                      (item) => !isEqual(item.value, option.value),
                    );

                    setValue(
                      `overrideStaffVisitDuration.${option.value}`,
                      undefined,
                    );
                    onChange([...updatedVisitTypes]);
                  }}
                  error={error?.message}
                />

                {value.length > 0 && (
                  <ItemsArea
                    label="Wybrane typy wizyt"
                    disabled={isEditMode}
                    selectedItems={value}
                    onRemove={(option) => {
                      const updatedVisitTypes = value.filter(
                        (item) => !isEqual(item.value, option.value),
                      );

                      setValue(
                        `overrideStaffVisitDuration.${option.value}`,
                        undefined,
                      );
                      onChange([...updatedVisitTypes]);
                    }}
                  />
                )}
              </div>
            )}
          />

          <div className="flex w-1/2 flex-col gap-2.5">
            {isEditMode ? (
              <div className="h-18" />
            ) : (
              <Controller
                control={control}
                name="isCustomVisitDuration"
                render={({
                  field: { onChange, value, ...restField },
                  fieldState: { error },
                }) => (
                  <div className="pt-7 pl-2">
                    <Checkbox
                      label="Niestandardowa długość wizyty"
                      checked={value}
                      disabled={isEditMode}
                      error={Boolean(error)}
                      onChange={() => {
                        onChange(!value);
                      }}
                      {...restField}
                    />
                  </div>
                )}
              />
            )}

            {isCustomVisitDuration && selectedVisitTypes.length > 0 && (
              <OverrideVisitDurationInputGroup
                control={control}
                selectedVisitTypes={selectedVisitTypes}
                disabled={isEditMode}
              />
            )}
          </div>
        </div>

        <div className="flex gap-4">
          {!isEditMode && (
            <Controller
              control={control}
              name="changeRepeating"
              render={({
                field: { onChange, value },
                fieldState: { error },
              }) => (
                <div className="w-[340px]">
                  <Select
                    label="Powtarzanie zmiany*"
                    placeholder="Wybierz z listy"
                    options={changeRepeatingOptions}
                    value={
                      changeRepeatingOptions.find((o) => o.value === value) ??
                      null
                    }
                    onChange={(option) => {
                      if (!option) {
                        return;
                      }

                      setTimeout(() => {
                        onChange(option.value);
                        setValue("availableDays", []);
                      }, 100);
                    }}
                    error={error?.message}
                  />
                </div>
              )}
            />
          )}

          <div className="flex items-start gap-1">
            <Controller
              control={control}
              name="dateStartFrom"
              render={({
                field: { onChange, value },
                fieldState: { error },
              }) => (
                <DateInput
                  label={
                    selectedChangeRepeating === "DO_NOT_REPEAT"
                      ? "Data*"
                      : "Data rozpoczęcia*"
                  }
                  data-testid={StaffScheduleDataTestId.DateStartFromInput}
                  format="yyyy-MM-dd"
                  maxDate={startDate.endOf("month").toDate()}
                  minDate={startDate.startOf("month").toDate()}
                  clearIcon={null}
                  dayPlaceholder="DD"
                  monthPlaceholder="MM"
                  yearPlaceholder="RRRR"
                  calendarIcon={<CalendarAddIcon size="sm" />}
                  locale="pl"
                  value={value}
                  disabled={isEditMode}
                  onChange={(e) => {
                    onChange(e);
                    setValue("availableDays", []);
                  }}
                  error={error?.message}
                />
              )}
            />

            {selectedChangeRepeating !== "DO_NOT_REPEAT" && (
              <Controller
                control={control}
                name="dateEnded"
                render={({
                  field: { onChange, value },
                  fieldState: { error },
                }) => (
                  <DateInput
                    label="Data zakończenia*"
                    format="yyyy-MM-dd"
                    maxDate={startDate.endOf("month").toDate()}
                    minDate={startDate.startOf("month").toDate()}
                    clearIcon={null}
                    dayPlaceholder="DD"
                    monthPlaceholder="MM"
                    yearPlaceholder="RRRR"
                    calendarIcon={<CalendarAddIcon size="sm" />}
                    locale="pl"
                    value={value}
                    disabled={isEditMode}
                    onChange={(e) => {
                      onChange(e);
                      setValue("availableDays", []);
                    }}
                    error={error?.message}
                  />
                )}
              />
            )}
          </div>
        </div>

        {holidaysInRange.length > 0 && (
          <MessageBox
            color="orange"
            text={`Dni wolne od pracy zostaną pominięte (${holidaysInRange.join(", ")})`}
          />
        )}

        <div className="flex flex-col gap-1">
          <span className="font-paragraph-1 font-normal">
            Przedział czasowy*
          </span>

          <div className="flex gap-2">
            <Controller
              control={control}
              name="timeFrom"
              render={({
                field: { onChange, value },
                fieldState: { error },
              }) => (
                <TimeInput
                  data-testId={StaffScheduleDataTestId.TimeFromInput}
                  ref={timeFromInputRef}
                  disabled={isEditMode}
                  error={Boolean(timeToFieldError || error)}
                  minutesStep={5}
                  value={value}
                  onChange={onChange}
                  onComplete={() => timeToInputRef.current?.focus()}
                />
              )}
            />

            <Controller
              control={control}
              name="timeTo"
              render={({
                field: { onChange, value },
                fieldState: { error },
              }) => (
                <TimeInput
                  data-testId={StaffScheduleDataTestId.TimeToInput}
                  ref={timeToInputRef}
                  disabled={isEditMode}
                  error={Boolean(timeToFieldError || error)}
                  minutesStep={5}
                  value={value}
                  onChange={onChange}
                  onBackspaceAtEmpty={() => timeFromInputRef.current?.focus()}
                />
              )}
            />
          </div>

          {Boolean(timeToFieldError || timeFromFieldError) && (
            <ErrorMessage
              error={timeToFieldError?.message || timeFromFieldError?.message}
            />
          )}
        </div>

        {selectedChangeRepeating === "CUSTOM" && (
          <Controller
            control={control}
            name="availableDays"
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <div className="flex flex-col gap-1">
                <span className="font-label">Wybierz dni*</span>

                <div
                  className={`flex gap-4 rounded-md p-4 ${error ? "outline-jutro-new-rose-600 outline outline-2 outline-offset-0" : "border-jutro-new-warm-gray-200 border"}`}
                >
                  {daysOptions.map((option) => (
                    <ToggleButton
                      key={option.value}
                      text={option.label}
                      active={value.includes(option.value)}
                      disabled={
                        !dateEnded ||
                        Boolean(dateEndedError) ||
                        !isDayInRange(option.value, dateStartFrom, dateEnded)
                      }
                      size="condensed"
                      onClick={() => {
                        if (value.includes(option.value)) {
                          onChange(value.filter((v) => v !== option.value));
                          return;
                        }

                        onChange([...value, option.value]);
                      }}
                    />
                  ))}
                </div>

                {error && <ErrorMessage error={error.message} />}
              </div>
            )}
          />
        )}

        <div>
          <Controller
            control={control}
            name="comment"
            render={({ field: { onChange, value } }) => (
              <Input
                data-testId={StaffScheduleDataTestId.CommentInput}
                label="Komentarz"
                placeholder="np. szczegóły dotyczące urlopu lub powodu nadgodzin"
                disabled={isEditMode}
                value={value ?? ""}
                onChange={onChange}
              />
            )}
          />
        </div>
      </form>

      <div className="flex justify-between">
        <ButtonsController
          isEditMode={isEditMode}
          isDeletable={isDeletable}
          isDeleteBlocked={isDeleteBlocked}
          isActiveOrganization={isActiveOrganization}
          loading={loading}
          onCloseModal={onCloseModal}
          handleSubmit={handleSubmit}
          onSubmit={onSubmit}
        />
      </div>
    </>
  );
};
